import { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { useShallow } from 'zustand/react/shallow';
import { useResetModuleUiOnBlur } from '../../shell/navigation/useResetModuleUiOnBlur';
import { resetAllInventorySlices, useInventoryUiStore } from '../store/index';
import { showInfo, showError } from '../../shared/ui/appToast';
import { useSyncBranchSettingsOnFocus } from '../../shell/session/hooks/useSyncBranchSettingsOnFocus';
import { isInventoriableProduct, getStockMovementReasonLabel, STOCK_MOVEMENT_REASONS, buildStockListCsv, getStockByBranch } from '../../inventory/stock/public-api';
import type { Product } from '../../catalog/domain-api';
import { useInventoryMasterData } from '../application/useInventoryMasterData'
import { useInventoryStockListFlow } from '../application/useInventoryStockListFlow';
import { useInventoryKpis } from '../application/useInventoryKpis';
import { useInventoryNoSalesAlert } from '../application/useInventoryNoSalesAlert';
import { useInventoryScreenContext } from './hooks/useInventoryScreenContext';
import { useInventoryFlows } from './hooks/useInventoryFlows';
import { useInventoryLoaders } from './hooks/useInventoryLoaders';
import { useInventoryProductSearch } from './hooks/useInventoryProductSearch';
import { useInventoryModals } from './hooks/useInventoryModals';
import { useInventoryBarcodeHandlers } from './hooks/useInventoryBarcodeHandlers';
import { useInventoryRouteFocus } from './hooks/useInventoryRouteFocus';
import { useInventoryInitialRouteFilters } from './hooks/useInventoryInitialRouteFilters';
import { shareOrDownloadCsv } from '../../shared/csv-core/infra/shareCsv';

export const useInventoryScreen = () => {
  useSyncBranchSettingsOnFocus();

  const ctx = useInventoryScreenContext();
  const { currentBranchId, user, canEdit, canAdjust, canTransfer, canCount, canViewValuation, destinationBranches, stockHistoryRemoteOptions, currentBranchName, actorLabel, openCatalog } = ctx;
  const stockDataRevision = useInventoryUiStore((s) => s.stockDataRevision);
  const [transferPickerScannerVisible, setTransferPickerScannerVisible] = useState(false);

  const transfers = useInventoryUiStore(
    useShallow((s) => ({
      transferModalVisible: s.transferModalVisible,
      transferPickerVisible: s.transferPickerVisible,
      transferPickerSearch: s.transferPickerSearch,
      selectedStock: s.selectedStock,
      destinationBranchId: s.destinationBranchId,
      transferQuantity: s.transferQuantity,
      transferWaitForReceive: s.transferWaitForReceive,
      isSubmittingTransfer: s.isSubmittingTransfer,
      pendingTransfers: s.pendingTransfers,
      pendingTransfersLoading: s.pendingTransfersLoading,
      pendingTransfersModalVisible: s.pendingTransfersModalVisible,
    }))
  );
  const history = useInventoryUiStore(
    useShallow((s) => ({
      historyVisible: s.historyVisible,
      historySelectedStock: s.historySelectedStock,
      historyLoading: s.historyLoading,
      movementHistory: s.movementHistory,
      historyOffset: s.historyOffset,
      historyHasMore: s.historyHasMore,
      historyLoadingMore: s.historyLoadingMore,
    }))
  );
  const bulkPhysical = useInventoryUiStore(
    useShallow((s) => ({
      bulkCoeffVisible: s.bulkCoeffVisible,
      bulkCategory: s.bulkCategory,
      bulkSearch: s.bulkSearch,
      bulkPercentInput: s.bulkPercentInput,
      bulkApplyTo: s.bulkApplyTo,
      bulkScannerVisible: s.bulkScannerVisible,
      bulkPreview: s.bulkPreview,
      bulkPreviewLoading: s.bulkPreviewLoading,
      bulkSaving: s.bulkSaving,
      physicalCountVisible: s.physicalCountVisible,
      physicalCountRows: s.physicalCountRows,
      physicalCountLoading: s.physicalCountLoading,
      physicalCountSaving: s.physicalCountSaving,
    }))
  );
  const scanners = useInventoryUiStore(
    useShallow((s) => ({
      searchScannerVisible: s.searchScannerVisible,
      entryScannerVisible: s.entryScannerVisible,
      lookupFeedback: s.lookupFeedback,
      activityModalVisible: s.activityModalVisible,
      addStockVisible: s.addStockVisible,
    }))
  );
  const setters = useInventoryUiStore(
    useShallow((s) => ({
      setTransferPickerSearch: s.setTransferPickerSearch,
      setSelectedStock: s.setSelectedStock,
      setDestinationBranchId: s.setDestinationBranchId,
      setTransferQuantity: s.setTransferQuantity,
      setTransferWaitForReceive: s.setTransferWaitForReceive,
      setSearchScannerVisible: s.setSearchScannerVisible,
      setEntryScannerVisible: s.setEntryScannerVisible,
      setLookupFeedback: s.setLookupFeedback,
      setBulkCoeffVisible: s.setBulkCoeffVisible,
      setBulkCategory: s.setBulkCategory,
      setBulkSearch: s.setBulkSearch,
      setBulkPercentInput: s.setBulkPercentInput,
      setBulkApplyTo: s.setBulkApplyTo,
      setBulkScannerVisible: s.setBulkScannerVisible,
      setBulkPreview: s.setBulkPreview,
      setPhysicalCountVisible: s.setPhysicalCountVisible,
      setActivityModalVisible: s.setActivityModalVisible,
    }))
  );

  const kpis = useInventoryKpis(currentBranchId);
  const noSalesAlert = useInventoryNoSalesAlert(currentBranchId);
  const stockListFlow = useInventoryStockListFlow({
    currentBranchId,
    noSalesProductIds: noSalesAlert.productIds,
  });
  const { loadStocks, stocks, filteredStocks } = stockListFlow;
  const masterData = useInventoryMasterData();

  // Recargar stock cuando otro módulo (ej. Compras) notifica que el stock cambió.
  useEffect(() => {
    if (stockDataRevision > 0) {
      void loadStocks();
    }
  }, [stockDataRevision, loadStocks]);

  const loaders = useInventoryLoaders({
    currentBranchId: currentBranchId ?? null,
    loadStocks,
    refreshKpis: async () => {
      await Promise.all([kpis.refresh(), noSalesAlert.refresh()]);
    },
    stockHistoryRemoteOptions,
  });

  const productSearch = useInventoryProductSearch({
    stocks,
    handleSelectProduct: (product: Product) => {
      if (!isInventoriableProduct(product)) {
        showInfo('Servicio sin stock', 'Los servicios no llevan stock.');
        return;
      }
      useInventoryUiStore.getState().setEntrySupplierId(product.preferredSupplierId ?? null);
    },
  });
  const { setLastUnitCost, setSavingEntry, selectedProduct, entryQuantity, entryUnitCost, entryMinQuantity, entryExpiry, entryReason, entryLotNumber, entrySupplierId, entryVariantId, availableVariants } = productSearch;

  const modals = useInventoryModals({
    canEdit,
    canAdjust,
    canViewValuation,
    currentBranchId: currentBranchId ?? null,
    stocks,
    openCatalog,
    setLastUnitCost,
  });
  const {
    closeAddStockModal,
    closeAdjustModal,
    closeCostCorrectModal,
    closeActivityModal,
    openAdjustModal,
    openExpiryWriteOffModal,
    openCostCorrectModal,
    openAddStockModalForProduct,
    openAddStockModal,
    openAddStockForStockRow,
    openActivityModal: openActivityModalUi,
  } = modals;

  const openActivityModal = useCallback(() => {
    openActivityModalUi();
    void loaders.loadBranchActivity();
  }, [openActivityModalUi, loaders.loadBranchActivity]);

  const flows = useInventoryFlows({
    ctx: { currentBranchId, user, canEdit, canAdjust, canTransfer, canCount, canViewValuation, destinationBranches, stockHistoryRemoteOptions, currentBranchName, actorLabel },
    stocks,
    filteredStocks,
    loadStocks,
    loadPendingTransfers: loaders.loadPendingTransfers,
    loadBranchActivity: loaders.loadBranchActivity,
    loadInventoryDashboard: kpis.refresh,
    refreshKpis: kpis.refresh,
    closeAdjustModal,
    closeCostCorrectModal,
    closeAddStockModal,
    closeActivityModal,
    openAddStockModalForProduct,
    selectedProduct,
    entryQuantity,
    entryUnitCost,
    entryMinQuantity,
    entryExpiry,
    entryReason,
    entryLotNumber,
    entrySupplierId,
    entryVariantId,
    setSavingEntry,
  });

  useInventoryRouteFocus({
    currentBranchId,
    openAdjustModal,
    openAddStockModalForProduct,
    openAddStockModal,
  });
  useInventoryInitialRouteFilters({ applyStockQuickViewFilter: stockListFlow.applyStockQuickViewFilter });

  const barcodeHandlers = useInventoryBarcodeHandlers({
    searchQuery: stockListFlow.searchQuery,
    productQuery: productSearch.productQuery,
    bulkSearch: bulkPhysical.bulkSearch,
    transferPickerSearch: transfers.transferPickerSearch,
    setSearchQuery: stockListFlow.setSearchQuery,
    setProductQuery: productSearch.setProductQuery,
    setLookupFeedback: setters.setLookupFeedback,
    setEntryScanFeedback: productSearch.setEntryScanFeedback,
    setEntrySuggestion: productSearch.setEntrySuggestion,
    setSelectedProduct: productSearch.setSelectedProduct,
    setEntryMinQuantity: productSearch.setEntryMinQuantity,
    setEntrySupplierId: productSearch.setEntrySupplierId,
    setTransferPickerSearch: setters.setTransferPickerSearch,
    setSearchScannerVisible: setters.setSearchScannerVisible,
    setEntryScannerVisible: setters.setEntryScannerVisible,
    setBulkScannerVisible: setters.setBulkScannerVisible,
    setTransferPickerScannerVisible,
    clearPinnedStockSearch: stockListFlow.clearPinnedStockSearch,
    stocks,
    handleBulkScan: flows.handleBulkScan,
  });

  const { transferFlow } = flows;

  useResetModuleUiOnBlur(
    useCallback(() => {
      flows.closeDetailModal();
      setTransferPickerScannerVisible(false);
      resetAllInventorySlices();
    }, [flows.closeDetailModal])
  );

  const { stockBrandOptions, stockSupplierOptions, inventoryStockCategoryFilters, bulkCategoryOptions } = masterData;

  const exportStockListCsv = useCallback(async () => {
    if (!currentBranchId) {
      showInfo('Exportar', 'Seleccioná una sucursal para exportar el stock.');
      return;
    }
    try {
      const allRows = await getStockByBranch(currentBranchId, {
        search: stockListFlow.stockListSearchQuery || undefined,
        statusFilter: stockListFlow.stockFilter,
        categoryId: stockListFlow.stockCategoryFilter !== 'all' ? stockListFlow.stockCategoryFilter : undefined,
        brandId: stockListFlow.stockBrandFilter ?? undefined,
        supplierId: stockListFlow.stockSupplierFilter ?? undefined,
        pinnedProductId: stockListFlow.pinnedStockProduct?.id,
        productIdAllowlist:
          stockListFlow.stockFilter === 'no_sales' ? [...noSalesAlert.productIds] : undefined,
        sort: stockListFlow.stockSort,
        includeVariants: true,
      });
      if (allRows.length === 0) {
        showInfo('Exportar', 'No hay productos de stock para exportar con estos filtros.');
        return;
      }
      const csv = buildStockListCsv(allRows);
      await shareOrDownloadCsv(csv, 'listado_stock.csv');
    } catch (e) {
      console.warn('[Inventory] export stock list CSV', e);
      showError('Exportar', 'No pudimos generar el archivo. Reintentá.');
    }
  }, [
    currentBranchId,
    stockListFlow.stockListSearchQuery,
    stockListFlow.stockFilter,
    stockListFlow.stockCategoryFilter,
    stockListFlow.stockBrandFilter,
    stockListFlow.stockSupplierFilter,
    stockListFlow.pinnedStockProduct?.id,
    stockListFlow.stockSort,
    noSalesAlert.productIds,
  ]);

  return {
    stockList: {
      stocks: stockListFlow.stocks,
      stockPageIndex: stockListFlow.stockPageIndex,
      stockPageSize: stockListFlow.stockPageSize,
      setStockPageSize: stockListFlow.setStockPageSize,
      stockHasMore: stockListFlow.stockHasMore,
      stockLoading: stockListFlow.stockLoading,
      stockError: stockListFlow.stockError,
      stockResultTotalCount: stockListFlow.stockResultTotalCount,
      searchQuery: stockListFlow.searchQuery,
      setSearchQuery: stockListFlow.setSearchQuery,
      onInventorySearchFieldChange: stockListFlow.onInventorySearchFieldChange,
      stockCategoryFilter: stockListFlow.stockCategoryFilter,
      applyStockCategoryFilter: stockListFlow.applyStockCategoryFilter,
      stockBrandFilter: stockListFlow.stockBrandFilter,
      stockSupplierFilter: stockListFlow.stockSupplierFilter,
      applyStockBrandFilter: stockListFlow.applyStockBrandFilter,
      applyStockSupplierFilter: stockListFlow.applyStockSupplierFilter,
      stockBrandOptions,
      stockSupplierOptions,
      inventoryStockCategoryFilters,
      pinnedStockProduct: stockListFlow.pinnedStockProduct,
      clearPinnedStockSearch: stockListFlow.clearPinnedStockSearch,
      handlePickInventorySearchSuggestion: stockListFlow.handlePickInventorySearchSuggestion,
      stockFilter: stockListFlow.stockFilter,
      applyStockQuickViewFilter: stockListFlow.applyStockQuickViewFilter,
      stockSort: stockListFlow.stockSort,
      applyStockSort: stockListFlow.applyStockSort,
      exportStockListCsv,
      filteredStocks: stockListFlow.filteredStocks,
      loadStocks: stockListFlow.loadStocks,
      loadStockNextPage: stockListFlow.loadStockNextPage,
      loadStockPreviousPage: stockListFlow.loadStockPreviousPage,
      loadStockJumpToPage: stockListFlow.loadStockJumpToPage,
      inventoryListRef: stockListFlow.inventoryListRef,
      inventoryWideScrollRef: stockListFlow.inventoryWideScrollRef,
      // #801: Expand/collapse variant rows
      expandedStockProductIds: stockListFlow.expandedStockProductIds,
      toggleStockProductExpanded: stockListFlow.toggleStockProductExpanded,
      variantRowsByProduct: stockListFlow.variantRowsByProduct,
      variantRowsErrorByProduct: stockListFlow.variantRowsErrorByProduct,
      retryVariantRows: stockListFlow.retryVariantRows,
    },
    global: {
      currentBranchId: ctx.currentBranchId,
      branches: ctx.branches,
      user: ctx.user,
      canEdit: ctx.canEdit,
      canAdjust: ctx.canAdjust,
      canTransfer: ctx.canTransfer,
      canCount: ctx.canCount,
      canViewValuation: ctx.canViewValuation,
      navigation: ctx.navigation,
      catalogProductCount: ctx.catalogProductCount,
      retryCatalogProductCount: ctx.retryCatalogProductCount,
      inventoryDashboard: kpis.dashboard,
      /** Error del último refresh de KPIs — la UI muestra aviso + retry, nunca ceros. */
      kpisError: kpis.error,
      kpisLoading: kpis.loading,
      retryKpis: kpis.refresh,
      noSalesAlert: {
        days: noSalesAlert.days,
        count: noSalesAlert.count,
        productIds: noSalesAlert.productIds,
      },
      loadFn: loaders.loadFn,
      loadInventoryDashboard: kpis.refresh,
      autoFocusInventorySearchOnWeb: false,
      actorLabel: ctx.actorLabel,
      getStockMovementReasonLabel,
      STOCK_MOVEMENT_REASONS,
      openCatalog: ctx.openCatalog,
      barcodeAllowCamera: barcodeHandlers.barcodeAllowCamera,
      barcodeAllowHardware: barcodeHandlers.barcodeAllowHardware,
      handleInventorySearchScan: barcodeHandlers.handleInventorySearchScan,
      handleMainSearchHardwareSubmit: barcodeHandlers.handleMainSearchHardwareSubmit,
    },
    transfers: {
      ...transfers,
      setTransferPickerSearch: setters.setTransferPickerSearch,
      setSelectedStock: setters.setSelectedStock,
      destinationBranches,
      setDestinationBranchId: setters.setDestinationBranchId,
      setTransferQuantity: setters.setTransferQuantity,
      setTransferWaitForReceive: setters.setTransferWaitForReceive,
      loadPendingTransfers: loaders.loadPendingTransfers,
      ...transferFlow,
      transferPickerScannerVisible,
      setTransferPickerScannerVisible,
      handleTransferPickerScan: barcodeHandlers.handleTransferPickerScan,
      handleTransferPickerSearchHardwareSubmit: barcodeHandlers.handleTransferPickerSearchHardwareSubmit,
    },
    addStock: {
      addStockVisible: scanners.addStockVisible,
      productQuery: productSearch.productQuery,
      setProductQuery: productSearch.setProductQuery,
      productQueryDebounced: productSearch.productQueryDebounced,
      productResults: productSearch.productResults,
      productPageOffset: productSearch.productPageOffset,
      productHasMore: productSearch.productHasMore,
      productLoadingMore: productSearch.productLoadingMore,
      selectedProduct: productSearch.selectedProduct,
      setSelectedProduct: productSearch.setSelectedProduct,
      entryQuantity: productSearch.entryQuantity,
      setEntryQuantity: productSearch.setEntryQuantity,
      entryUnitCost: productSearch.entryUnitCost,
      setEntryUnitCost: productSearch.setEntryUnitCost,
      entryMinQuantity: productSearch.entryMinQuantity,
      setEntryMinQuantity: productSearch.setEntryMinQuantity,
      entryExpiry: productSearch.entryExpiry,
      setEntryExpiry: productSearch.setEntryExpiry,
      entryReason: productSearch.entryReason,
      setEntryReason: productSearch.setEntryReason,
      entrySupplierId: productSearch.entrySupplierId,
      setEntrySupplierId: productSearch.setEntrySupplierId,
      entryLotNumber: productSearch.entryLotNumber,
      setEntryLotNumber: productSearch.setEntryLotNumber,
      entryVariantId: productSearch.entryVariantId,
      setEntryVariantId: productSearch.setEntryVariantId,
      availableVariants: productSearch.availableVariants,
      savingEntry: productSearch.savingEntry,
      lastUnitCost: productSearch.lastUnitCost,
      suppliers: stockSupplierOptions,
      openAddStockModal,
      openAddStockModalForProduct,
      openAddStockForStockRow,
      closeAddStockModal,
      handleAddStock: flows.handleAddStock,
      handleEntryScan: barcodeHandlers.handleEntryScan,
      entryScanFeedback: productSearch.entryScanFeedback,
      entrySuggestion: productSearch.entrySuggestion,
      setEntrySuggestion: productSearch.setEntrySuggestion,
      entryScannerVisible: scanners.entryScannerVisible,
      setEntryScannerVisible: setters.setEntryScannerVisible,
      handleEntryProductQueryHardwareSubmit: barcodeHandlers.handleEntryProductQueryHardwareSubmit,
      loadMoreProducts: productSearch.loadMoreProducts,
      handleSelectProduct: productSearch.handleSelectProduct,
      clearSelectedProduct: productSearch.clearSelectedProduct,
    },
    adjustment: {
      adjustModalVisible: flows.adjustModalVisible,
      adjustStockRow: flows.adjustStockRow,
      setAdjustStockRow: flows.setAdjustStockRow,
      adjustDelta: flows.adjustDelta,
      setAdjustDelta: flows.setAdjustDelta,
      adjustReason: flows.adjustReason,
      setAdjustReason: flows.setAdjustReason,
      adjustSaving: flows.adjustSaving,
      openAdjustModal,
      openExpiryWriteOffModal,
      closeAdjustModal,
      applyAdjustment: flows.applyAdjustment,
      costCorrectModalVisible: flows.costCorrectModalVisible,
      costCorrectStockRow: flows.costCorrectStockRow,
      costCorrectInput: flows.costCorrectInput,
      setCostCorrectInput: flows.setCostCorrectInput,
      costCorrectSaving: flows.costCorrectSaving,
      openCostCorrectModal,
      closeCostCorrectModal,
      applyCostCorrect: flows.applyCostCorrect,
    },
    bulkPhysical: {
      ...bulkPhysical,
      setBulkCoeffVisible: setters.setBulkCoeffVisible,
      setBulkCategory: setters.setBulkCategory,
      setBulkSearch: setters.setBulkSearch,
      setBulkPercentInput: setters.setBulkPercentInput,
      setBulkApplyTo: setters.setBulkApplyTo,
      setBulkScannerVisible: setters.setBulkScannerVisible,
      setBulkPreview: setters.setBulkPreview,
      setPhysicalCountVisible: setters.setPhysicalCountVisible,
      bulkCategoryOptions,
      handleBulkSearchHardwareSubmit: barcodeHandlers.handleBulkSearchHardwareSubmit,
      openBulkCoeffModal: flows.openBulkCoeffModal,
      handleBulkScan: flows.handleBulkScan,
      loadBulkPreview: flows.loadBulkPreview,
      applyBulkCoefficient: flows.applyBulkCoefficient,
      openPhysicalCountModal: flows.openPhysicalCountModal,
      resolvePhysicalCountScanFilter: flows.resolvePhysicalCountScanFilter,
      setPhysicalCountedQty: flows.setPhysicalCountedQty,
      applyPhysicalCountDifferences: flows.applyPhysicalCountDifferences,
    },
    historyActivity: {
      ...history,
      branchActivity: loaders.branchActivity,
      branchActivityLoading: loaders.branchActivityLoading,
      loadBranchActivity: loaders.loadBranchActivity,
      openActivityModal,
      closeActivityModal,
      activityModalVisible: scanners.activityModalVisible,
      setActivityModalVisible: setters.setActivityModalVisible,
      historyReasonFilter: flows.historyReasonFilter,
      setHistoryReasonFilter: flows.setHistoryReasonFilter,
      historyFromDate: flows.historyFromDate,
      setHistoryFromDate: flows.setHistoryFromDate,
      historyToDate: flows.historyToDate,
      setHistoryToDate: flows.setHistoryToDate,
      historyReasonOptions: flows.historyReasonOptions,
      openHistoryModal: flows.openHistoryModal,
      loadMoreHistory: flows.loadMoreHistory,
      closeHistoryModal: flows.closeHistoryModal,
      applyHistoryFilters: flows.applyHistoryFilters,
      exportMovementHistoryCsv: flows.exportMovementHistoryCsv,
      exportingHistory: flows.exportingHistory,
      openSaleFromMovement: flows.openSaleFromMovement,
      openStockFromBranchActivity: flows.openStockFromBranchActivity,
    },
    uiState: {
      searchScannerVisible: scanners.searchScannerVisible,
      setSearchScannerVisible: setters.setSearchScannerVisible,
      lookupFeedback: scanners.lookupFeedback,
      setLookupFeedback: setters.setLookupFeedback,
      detailStock: flows.detailStock,
      detailVisible: flows.detailVisible,
      openDetailModal: flows.openDetailModal,
      closeDetailModal: flows.closeDetailModal,
    },
  };
};

export type InventoryScreenViewModel = ReturnType<typeof useInventoryScreen>;
